import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { TokenTransactionListView } from '@app/feature/token/token-transaction-list/token-transaction-list.view';
import { TRANSACTION_LIST_SORT_FIELD } from '@app/feature/transaction/transaction-list/transaction-list.config';
import { useCpmsBoGetTransactionsPaginatedQuery } from '@service/cpms-bo-transaction.service';

import {
  TokenTransactionListContextProvider,
  useTokenContext,
  useTokenTransactionListContext,
} from '../token.context';
import * as css from './token-transaction-list.module.scss';

export const TokenTransactionListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
  } = useTokenTransactionListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  // Nothing to do

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetTransactionsPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? TRANSACTION_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GatewayGetTransactionsPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: TokenTransactionListViewProtected } =
    withProtectedResource(TokenTransactionListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.TRANSACTIONS,
          action: ActionsByResource.TRANSACTIONS.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id:
            getPaginatedQueryParameters.sortField ??
            TRANSACTION_LIST_SORT_FIELD,
        },
      ],
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {TokenTransactionListViewProtected}
    </Stack>
  );
};

export const TokenTransactionListControllerWithContext =
  (): React.JSX.Element => {
    const token = useTokenContext();

    return (
      <TokenTransactionListContextProvider data={{ token }}>
        <TokenTransactionListController />
      </TokenTransactionListContextProvider>
    );
  };
